import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer } from '@manifesto-tech/react-leaflet';
import 'projektpro-leaflet-smoothwheelzoom';
import MapBoundary from './MapBoundary.jsx';
import Markers from './Markers.jsx';
import Sidebar from './Sidebar.jsx';
import Search from './Search.jsx';
import { getFeatures } from '../utils/utilities';

export default function LeafletMap({ mapId, baseApiUrl, blockHeading }) {
  const mapWrapper = useRef(null);
  const [map, setMap] = useState(null);
  const [locationData, setLocationData] = useState({});
  const [locationProperties, setLocationProperties] = useState({ printable_map_url: '' });
  const [markerData, setMarkerData] = useState({});
  const [markers, setMarkers] = useState({});
  const [visibleMarkers, setVisibleMarkers] = useState({});
  const [sidebarMarkers, setSidebarMarkers] = useState(markers);
  const [selectedMarkerButton, setSelectedMarkerButton] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeMarkerGroup, setActiveMarkerGroup] = useState('');
  const [mapIsVisible, setIsVisible] = useState(false);

  const popupClickHandler = (popup, e) => {
    const clickedMarker =
      popup && popup._source && popup._source._icon ? popup._source._icon : false;

    if (clickedMarker !== e.target && !popup._container.contains(e.target)) {
      popup.close();
    }
  };

  useEffect(() => {
    if (mapId) {
      fetch(`${baseApiUrl}/api/maps/locations/${mapId}`)
        .then((response) => response.json())
        .then((data) => {
          const locations =
            typeof data === 'string' || data instanceof String ? JSON.parse(data) : data;
          setLocationData(locations);
        });

      fetch(`${baseApiUrl}/api/maps/markers/${mapId}/all`)
        .then((response) => response.json())
        .then((data) => {
          const features =
            typeof data === 'string' || data instanceof String ? JSON.parse(data) : data;
          setMarkerData(features);
        });
    }
  }, []);

  useEffect(() => {
    if (Object.keys(markerData).length > 0) {
      const features = getFeatures(markerData);
      setMarkers(features);
      setVisibleMarkers(features);
      setSidebarMarkers(features);
    }
  }, [markerData]);

  useEffect(() => {
    if (Object.keys(locationData).length) {
      if (locationData.features.length) {
        const { properties } = locationData.features[0];
        setLocationProperties(properties);
      }
    }
  }, [locationData]);

  useEffect(() => {
    if (activeMarkerGroup === '') {
      setSelectedMarkerButton(false);
    }
  }, [activeMarkerGroup]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    });

    if (map) {
      const mapContainer = map.getContainer();
      map.SmoothWheelZoom = true;

      if (mapContainer) {
        observer.observe(mapContainer);
      }

      let popupClickListener = popupClickHandler;
      map.on('popupopen', ({ popup }) => {
        popupClickListener = popupClickHandler.bind([], popup);
        mapWrapper.current.classList.add(['leaflet-popup-open']);
        document.addEventListener('click', popupClickListener);
      });

      map.on('popupclose', () => {
        mapWrapper.current.classList.remove(['leaflet-popup-open']);
        document.removeEventListener('click', popupClickListener);
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [map]);

  useEffect(() => {
    if (mapIsVisible) {
      map.invalidateSize();
    }
  }, [mapIsVisible]);

  return (
    <div className="o-map__wrapper" ref={mapWrapper}>
      <div className="bg-sandstone">
        <div className="container flex max-md:flex-col max-md:pb-4">
          <div className="flex items-end md:w-2/3 mb-11 md:mr-5 lg:mr-9">
            {blockHeading && <h2>{blockHeading}</h2>}
          </div>
          <Search
            className={[
              'md:w-1/3',
              'flex-grow',
              'p-6',
              'bg-primary',
              'text-white',
              blockHeading && 'md:mt-16 lg:mt-24',
            ].join(' ')}
            markers={markers}
            visibleMarkers={visibleMarkers}
            setVisibleMarkers={setVisibleMarkers}
            sidebarMarkers={sidebarMarkers}
            setSidebarMarkers={setSidebarMarkers}
            selectedMarkerButton={selectedMarkerButton}
            setSelectedMarkerButton={setSelectedMarkerButton}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            setActiveMarkerGroup={setActiveMarkerGroup}
          />
        </div>
      </div>

      <div className="o-map__wrapper_main">
        <div className="container flex max-md:flex-col md:pt-4 lg:pt-8">
          <div className="flex flex-col w-full md:w-2/3 md:mr-5 lg:mr-9">
            <MapContainer
              className="w-full max-md:aspect-[1/1] md:min-h-lg lg:min-h-2xl flex-grow"
              ref={setMap}
              center={[51.505, -0.09]}
              zoom={14}
              zoomSnap={0}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a>"
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <>
                {Object.keys(locationData).length && <MapBoundary locationData={locationData} />}
                {Object.keys(visibleMarkers).length && <Markers markers={visibleMarkers} />}
              </>
            </MapContainer>
            {locationProperties.printable_map_url && (
              <a
                className="text-black text-sm font-normal underline mt-2 underline-offset-4"
                href={locationProperties.printable_map_url}
                target="_blank"
                rel="noreferrer"
              >
                Download a printable map
              </a>
            )}
          </div>

          {map && (
            <Sidebar
              className="o-map__sidebar md:w-1/3 flex-grow max-md:mt-4 md:max-h-xl lg:max-h-3xl overflow-y-scroll"
              map={map}
              visibleMarkers={visibleMarkers}
              setVisibleMarkers={setVisibleMarkers}
              sidebarMarkers={sidebarMarkers}
              selectedMarkerButton={selectedMarkerButton}
              setSelectedMarkerButton={setSelectedMarkerButton}
              searchInput={searchInput}
              activeMarkerGroup={activeMarkerGroup}
              setActiveMarkerGroup={setActiveMarkerGroup}
            />
          )}
        </div>
      </div>
    </div>
  );
}
